import React, { useState, useMemo, useRef } from "react";
import { FixedSizeList } from "react-window";
import { Check, Search } from "lucide-react";
import EllipsisTooltip from "@component/tooltip/EllipsisTooltip";
import { Checkbox } from "./checkbox";
import { IconInput } from "./IconInput";
import { Input } from "./input";

const ITEM_HEIGHT = 32;
const MAX_VISIBLE_SELECTED = 3;

function SearchableFilter({
  id,
  name = "",
  multiple = false,
  value = [],
  data = [],
  onChange = () => {},
  height
}) {
  const [inputValue, setInputValue] = useState("");
  const [showAllSelected, setShowAllSelected] = useState(false);
  const listRef = useRef(null);

  const handleOptionSelect = (option) => {
    if (multiple) {
      const newSelected = [...value];
      const index = newSelected.indexOf(option);
      if (index === -1) newSelected.push(option);
      else newSelected.splice(index, 1);
      onChange(newSelected);
    } else {
      onChange([option]);
    }
  };

  const groupedOptions = useMemo(() => {
    const lowerInput = inputValue.toLowerCase();

    const selectedItems = data.filter((d) => value.includes(d));
    const unselectedItems = data.filter((d) => !value.includes(d));

    const filteredData = unselectedItems.filter((d) =>
      d.toLowerCase().includes(lowerInput)
    );

    const selectedDisplay = showAllSelected
      ? selectedItems
      : selectedItems.slice(0, MAX_VISIBLE_SELECTED);

    const list = [];

    if (selectedDisplay.length > 0) {
      list.push({ type: "group", label: "Selected" });
      list.push(...selectedDisplay.map((v) => ({ type: "option", value: v })));
    }

    if (!showAllSelected && selectedItems.length > MAX_VISIBLE_SELECTED) {
      list.push({
        type: "toggle",
        label: `+${selectedItems.length - MAX_VISIBLE_SELECTED} more`,
      });
    }

    if (filteredData.length > 0) {
      list.push({ type: "group", label: "Unselected" });
      list.push(...filteredData.map((v) => ({ type: "option", value: v })));
    }

    if (list.length === 0) {
      list.push({ type: "empty", label: "No data found" });
    }

    return list;
  }, [value, showAllSelected, inputValue, data]);

  const renderRow = ({ index, style }) => {
    const item = groupedOptions[index];

    if (item.type === "group") {
      return (
        <li
          style={style}
          className="px-2 py-1 text-sm font-semibold list-none bg-gray-100 dark:bg-black/10"
        >
          {item.label}
        </li>
      );
    }

    if (item.type === "toggle") {
      return (
        <li
          style={style}
          className="px-2 py-1 text-sm italic bg-primary/20 text-primary text-gray-600 cursor-pointer list-none"
          onClick={() => setShowAllSelected(true)}
        >
          {item.label}
        </li>
      );
    }
        if (item.type === "empty") {
          return (
            <div
              style={style}
              className="text-center text-gray-500 py-2 text-sm font-medium"
            >
              {item.label}
            </div>
          );
        }

    const checked = value.includes(item.value);

    return (
      <li
        style={style}
        className={`flex items-center gap-2 px-2 py-1 cursor-pointer list-none`}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => handleOptionSelect(item.value)}
      >
          <Checkbox checked={checked} className='h-3.5 w-3.5' iconClassName='w-3.5 h-3.5' />
        <EllipsisTooltip className="text-xs"><span>{item.value}</span>
        </EllipsisTooltip>
      </li>
    );
  };
  const listHeight = (Math.min(groupedOptions.length, 10) * (ITEM_HEIGHT+8))

  return (
    <div className="w-full max-w-sm rounded-lg shadow-sm [&_*]:!font-sans ">
      {/* Search Input */}
      <div className="flex items-center px-2 py-2 border-b">

        <Search size={16} className="text-gray-500 mr-2 w-5 h-5" />
        <Input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          placeholder="Search actions..."
          className="w-full outline-none text-sm text-gray-700 dark:text-white placeholder-gray-400"
        />
      </div>

      {/* Dropdown list */}
 
        <FixedSizeList
          height={height ? height < listHeight ? height : listHeight : listHeight}
          itemCount={groupedOptions.length}
          itemSize={ITEM_HEIGHT}
          width="100%"
          ref={listRef}
        >
          {renderRow}
        </FixedSizeList>

    </div>
  );
}

export default React.memo(SearchableFilter);
